import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DataTable from '../../../sharedModules/dataTable';
import axios from '../../../sharedModules/utils/axios.utils';

const missingBoundsColumns = [
  {
    name: 'Vendor Name',
    selector: (row: any) => row.vendor_name || '',
    sortable: true,
    grow: 2,
  },
  {
    name: 'Vendor ID',
    selector: (row: any) => row.vendor_id || '—',
    sortable: true,
    width: '120px',
  },
  {
    name: 'Location',
    selector: (row: any) => row.location || '—',
    sortable: true,
    width: '110px',
  },
  {
    name: 'Invoice # Bounds',
    selector: (row: any) => row.has_invoice_number_bounds,
    sortable: true,
    width: '150px',
    cell: (row: any) => (
      <Chip
        label={row.has_invoice_number_bounds ? 'Set' : 'Missing'}
        color={row.has_invoice_number_bounds ? 'success' : 'error'}
        size="small"
      />
    ),
  },
  {
    name: 'Amount Bounds',
    selector: (row: any) => row.has_invoice_amount_bounds,
    sortable: true,
    width: '140px',
    cell: (row: any) => (
      <Chip
        label={row.has_invoice_amount_bounds ? 'Set' : 'Missing'}
        color={row.has_invoice_amount_bounds ? 'success' : 'error'}
        size="small"
      />
    ),
  },
  {
    name: 'Invoice # Pattern',
    selector: (row: any) => row.invoice_number_pattern || '—',
    sortable: true,
    grow: 2,
  },
  {
    name: 'Amount Pattern',
    selector: (row: any) => row.invoice_amount_pattern || '—',
    sortable: true,
    grow: 2,
  },
];

const unmatchedInvoicesColumns = [
  {
    name: 'Vendor Name',
    selector: (row: any) => row.vendor_name || '',
    sortable: true,
    grow: 2,
  },
  {
    name: 'Invoice #',
    selector: (row: any) => row.invoice_number || '—',
    sortable: true,
    width: '140px',
  },
  {
    name: 'Invoice Date',
    selector: (row: any) => (row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : '—'),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Total Amount',
    selector: (row: any) => (row.line_items_total != null ? `$${parseFloat(row.line_items_total).toFixed(2)}` : '—'),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Vendor ID',
    selector: (row: any) => row.vendor_id || '—',
    sortable: true,
    width: '110px',
  },
  {
    name: 'Location',
    selector: (row: any) => row.location || '—',
    sortable: true,
    width: '110px',
  },
  {
    name: 'Status',
    selector: (row: any) => row.validation_status || '',
    sortable: true,
    width: '130px',
    cell: (row: any) => <Chip label={row.validation_status || 'pending'} size="small" />,
  },
  {
    name: 'Created',
    selector: (row: any) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'),
    sortable: true,
    width: '120px',
  },
];

const VendorContactDetailAuditPage: React.FC = () => {
  const [missingBounds, setMissingBounds] = useState<any[]>([]);
  const [unmatchedInvoices, setUnmatchedInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuditData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get<string, any>('/vendors/vendor_contact_detail_audit');
        setMissingBounds(response?.data?.missing_bounds || []);
        setUnmatchedInvoices(response?.data?.unmatched_invoices || []);
      } catch (err) {
        console.error('Error fetching contact detail audit data:', err);
        setError('Failed to load audit data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, []);

  const header = (
    <Grid item xs={12} style={{ marginTop: 20 }}>
      <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
        Vendor Management
      </Typography>
      <NavigateNextIcon fontSize="small" style={{ margin: '-4px 4px' }} />
      <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
        Contact Detail Audit
      </Typography>
    </Grid>
  );

  if (loading) {
    return (
      <Grid container>
        {header}
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Loading audit data...</div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container>
        {header}
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <div style={{ color: 'red', textAlign: 'center', padding: '50px' }}>{error}</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      {header}

      <Grid item xs={12} style={{ marginTop: 24 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">Contacts Missing Bounds</Typography>
          <Chip label={missingBounds.length} color="error" size="small" />
        </Box>
        <Card>
          <CardContent>
            <DataTable
              title=""
              columns={missingBoundsColumns}
              data={missingBounds}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              highlightOnHover
              responsive
              dense
              noHeader
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 32 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">Invoices Without a Matching Contact Detail</Typography>
          <Chip label={unmatchedInvoices.length} color="warning" size="small" />
        </Box>
        <Card>
          <CardContent>
            <DataTable
              title=""
              columns={unmatchedInvoicesColumns}
              data={unmatchedInvoices}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              highlightOnHover
              responsive
              dense
              noHeader
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default VendorContactDetailAuditPage;
